'use client'

import { Cell, Image, Text } from '@vinted/web-ui'
import classNames from 'classnames'
import { useIntl } from 'react-intl'

import { useConversationContext } from '@marketplace-web/messaging/conversation-providers-feature'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { formatCurrencyAmount } from '@marketplace-web/currency/currency-data'
import {
  conversationItemPhotoClick,
  conversationMultipleItemsPhotosClick,
} from '@marketplace-web/messaging/conversation-messaging-data'

import HeroMessageActions from './HeroMessageActions'
import useTransactionActions from '../../hooks/useTransactionActions/useTransactionActions'
import HeroMessageUserProtection from './HeroMessageUserProtection'

type Props = {
  toggleSidebar?: () => void
  openOfferModal: () => void
}

enum ItemThumbnailSize {
  Small = 'thumb70x100',
  Medium = 'thumb150x210',
  Large = 'thumb310x430',
  XLarge = 'thumb364x428',
  X2Large = 'thumb624x428',
}

type ItemPhotoModel = {
  url: string
  imageNo: number
  isMain: boolean
  orientation?: number | null
  thumbnails: Array<{
    type: ItemThumbnailSize | string
    url: string
  }>
}

const getPhotoThumbnail = (photo: ItemPhotoModel, size: ItemThumbnailSize) => {
  const thumbnails = photo.thumbnails || []
  const thumbnail = thumbnails.find(({ type }) => type === size)

  if (!thumbnail) return null

  return thumbnail.url
}

const HeroMessage = ({ toggleSidebar, openOfferModal }: Props) => {
  const breakpoints = useBreakpoint()

  const { track } = useTracking()

  const { locale } = useIntl()
  const { conversation, transaction } = useConversationContext()
  const price = transaction?.offer.price
  const currency = price?.currencyCode

  const oppositeUser = conversation?.oppositeUser
  const displayedItem = conversation?.item
  const displayedItemPhoto = displayedItem?.photo
  const { applicableTransactionActions } = useTransactionActions()

  const itemsCount = transaction?.itemsCount || 0
  const itemUrl = displayedItem?.url

  const handleSingleItemPhotoClick = () => {
    if (!conversation?.transactionId || !conversation?.id) return

    track(
      conversationItemPhotoClick({
        transactionId: conversation?.transactionId,
        conversationId: String(conversation.id),
        userSide: conversation.transaction?.userSide ?? null,
        oppositeUserId: conversation.oppositeUser?.id ?? null,
      }),
    )
  }

  const handleMultipleItemPhotoClick = () => {
    if (!conversation?.transactionId || !conversation?.id) return

    track(
      conversationMultipleItemsPhotosClick({
        transactionId: conversation?.transactionId,
        conversationId: String(conversation.id),
        userSide: conversation.transaction?.userSide ?? null,
        oppositeUserId: conversation.oppositeUser?.id ?? null,
      }),
    )

    toggleSidebar?.()
  }

  const wrapComponentWithUrl = (component: JSX.Element) => {
    if (!itemUrl) return component

    return (
      <Cell
        styling={Cell.Styling.Tight}
        url={itemsCount === 1 ? itemUrl : undefined}
        onClick={itemsCount > 1 ? handleMultipleItemPhotoClick : handleSingleItemPhotoClick}
        clickable
        aria={{ 'aria-label': itemsCount > 1 ? transaction?.itemTitle : undefined }}
      >
        {component}
      </Cell>
    )
  }

  const renderPhotoLabel = () => {
    return itemsCount > 1 ? transaction?.itemsCount.toString() : undefined
  }

  const thumbnailUrl =
    displayedItemPhoto && getPhotoThumbnail(displayedItemPhoto, ItemThumbnailSize.Medium)

  const renderPhoto = () => {
    if (!displayedItemPhoto) return null

    const image = (
      <Image
        src={thumbnailUrl}
        styling={Image.Styling.Rounded}
        size={Image.Size.Large}
        scaling={Image.Scaling.Cover}
        label={renderPhotoLabel()}
        testId="hero-message-item-image"
      />
    )

    return wrapComponentWithUrl(image)
  }

  const renderTitle = () => {
    if (!transaction?.itemTitle) return null

    const text = <Text as="h2" text={transaction.itemTitle} type={Text.Type.Title} />

    return wrapComponentWithUrl(text)
  }

  const renderBody = () => {
    if (!price || !currency) return null

    const formattedPrice = formatCurrencyAmount(price, locale)

    return (
      <>
        <Text
          type={Text.Type.Subtitle}
          text={formattedPrice}
          testId="hero-message-price"
          theme="muted"
          as="p"
        />
        <HeroMessageUserProtection thumbnailUrl={thumbnailUrl} />
      </>
    )
  }

  const renderActions = () => {
    if (!displayedItem) return null

    return (
      <HeroMessageActions
        itemId={displayedItem.id}
        transaction={transaction}
        oppositeUser={oppositeUser}
        actions={applicableTransactionActions}
        openOfferModal={openOfferModal}
      />
    )
  }

  const actionsClass = classNames('u-ui-padding-horizontal-large', 'u-ui-padding-bottom-large')

  return (
    <>
      <Cell
        prefix={renderPhoto()}
        title={renderTitle()}
        body={renderBody()}
        suffix={breakpoints.tabletsUp && renderActions()}
        testId="hero-message"
      />

      {breakpoints.phones && <div className={actionsClass}>{renderActions()}</div>}
    </>
  )
}

export default HeroMessage
